// src/elements.ts
import { defineCustomElement } from "@aria-ui/core";

// src/listbox-empty.setup.ts
import {
  createComputed,
  useAriaAttribute
} from "@aria-ui/core";
import { usePresence } from "@aria-ui/presence";

// src/context.ts
import { createContext } from "@aria-ui/core";
var selectedValueContext = createContext(
  "ListboxItem/selectedValue",
  ""
);
var focusedValueContext = createContext("ListboxItem/focusedValue", "");
var availableValueSetContext = createContext(
  "Listbox/availableValueSet",
  null
);
var pointerMovingContext = createContext(
  "Listbox/pointerMoving",
  false
);
var listboxEmitterContext = createContext(
  "Listbox/listboxEmitter",
  null
);
var listboxItemEmitterContext = createContext(
  "Listbox/listboxItemEmitter",
  null
);

// src/listbox-empty.setup.ts
function useListboxEmpty(element) {
  const availableValueSet = availableValueSetContext.consume(element);
  const presence = createComputed(() => {
    var _a;
    return !((_a = availableValueSet.get()) == null ? void 0 : _a.size);
  });
  useAriaAttribute(element, "aria-disabled", () => {
    return presence.get() ? void 0 : "true";
  });
  useAriaAttribute(element, "aria-hidden", () => {
    return presence.get() ? void 0 : "true";
  });
  usePresence(element, presence);
}

// src/listbox-empty.types.ts
var listboxEmptyProps = {};
var listboxEmptyEvents = {};

// src/listbox-item.setup.ts
import {
  createComputed as createComputed2,
  useAriaAttribute as useAriaAttribute2,
  useAriaRole,
  useAttribute,
  useEffect,
  useEventListener
} from "@aria-ui/core";
import { usePresence as usePresence2 } from "@aria-ui/presence";
function useListboxItem(element, { state, emit }) {
  const selectedValue = selectedValueContext.consume(element);
  const focusedValue = focusedValueContext.consume(element);
  const pointerMoving = pointerMovingContext.consume(element);
  const listboxEmitter = listboxEmitterContext.consume(element);
  const listboxItemEmitter = listboxItemEmitterContext.consume(element);
  useAriaRole(element, "option");
  useEventListener(element, "pointerenter", () => {
    const value = state.value.get();
    const pointerMovingValue = pointerMoving.get();
    if (value == null || !pointerMovingValue) {
      return;
    }
    focusedValue.set(value);
  });
  useEventListener(element, "pointerdown", () => {
    var _a;
    const value = state.value.get();
    if (value == null) {
      return;
    }
    if (selectedValue.get() !== value) {
      selectedValue.set(value);
    }
    emit("select", void 0);
    (_a = listboxEmitter.get()) == null ? void 0 : _a();
  });
  const selected = createComputed2(() => {
    return !!state.value.get() && state.value.get() === selectedValue.get();
  });
  useAriaAttribute2(element, "aria-selected", () => {
    return selected.get() ? "true" : "false";
  });
  useAttribute(element, "data-selected", () => {
    return selected.get() ? "true" : void 0;
  });
  const focused = createComputed2(() => {
    return !!state.value.get() && state.value.get() === focusedValue.get();
  });
  useAttribute(element, "data-focused", () => {
    return focused.get() ? "true" : void 0;
  });
  const availableValueSet = availableValueSetContext.consume(element);
  const presence = createComputed2(() => {
    const availableValueSetValue = availableValueSet.get();
    if (!availableValueSetValue) {
      return true;
    }
    return availableValueSetValue.has(state.value.get() || "");
  });
  useAriaAttribute2(element, "aria-disabled", () => {
    return presence.get() ? void 0 : "true";
  });
  useAriaAttribute2(element, "aria-hidden", () => {
    return presence.get() ? void 0 : "true";
  });
  useEffect(element, () => {
    if (focused.get() && presence.get()) {
      listboxItemEmitter.set(() => {
        emit("select", void 0);
      });
    }
  });
  useEffect(element, () => {
    if (!presence.get()) {
      if (focused.get()) {
        focusedValue.set("");
      }
      if (selected.get()) {
        selectedValue.set("");
      }
    }
  });
  usePresence2(element, presence);
}

// src/listbox-item.types.ts
var listboxItemProps = {
  value: { default: "" }
};
var listboxItemEvents = {
  select: {}
};

// src/listbox.setup.ts
import { Collection } from "@aria-ui/collection";
import {
  createComputed as createComputed3,
  createSignal,
  useAriaRole as useAriaRole2,
  useEffect as useEffect2,
  useEventListener as useEventListener2,
  useQuerySelectorAll
} from "@aria-ui/core";
function useListbox(element, { state, emit }) {
  useAriaRole2(element, "listbox");
  const focusedValue = createSignal("");
  const selectedValue = state.value;
  selectedValueContext.provide(element, selectedValue);
  focusedValueContext.provide(element, focusedValue);
  pointerMovingContext.provide(element, useMouseMoving(element));
  const listboxEmitter = createSignal(() => {
    emit("valueChange", selectedValue.get());
  });
  listboxEmitterContext.provide(element, listboxEmitter);
  const listboxItemEmitter = createSignal(null);
  listboxItemEmitterContext.provide(element, listboxItemEmitter);
  const items = useQuerySelectorAll(element, '[role="option"]');
  const collection = createComputed3(() => {
    return new Collection(Array.from(items.get()));
  });
  const firstValue = createComputed3(() => {
    return collection.get().first();
  });
  useEffect2(element, () => {
    if (state.autoFocus.get()) {
      state.query.get();
      focusedValue.set(firstValue.get() || "");
    }
  });
  useEffect2(element, () => {
    element.tabIndex = 0;
  });
  const availableValues = createComputed3(() => {
    const queryValue = state.query.get();
    const values = collection.get().getValues();
    const filter = state.filter.get();
    const filteredValues = filter ? values.filter((value) => filter({ query: queryValue, value })) : values;
    return new Set(filteredValues);
  });
  availableValueSetContext.provide(element, availableValues);
  const available = createComputed3(() => availableValues.get().size > 0);
  useCollectionKeydownHandler(
    element,
    collection,
    focusedValue,
    selectedValue,
    state.eventTarget,
    available,
    listboxEmitter,
    listboxItemEmitter
  );
}
function useCollectionKeydownHandler(element, collection, focusedValue, selectedValue, eventTarget, available, listboxEmitter, listboxItemEmitter) {
  const scrollFocusedItemIntoView = () => {
    const target = collection.peek().getElement(focusedValue.get());
    target == null ? void 0 : target.scrollIntoView({ block: "nearest" });
  };
  const keydownHandler = (event) => {
    var _a, _b;
    if (event.defaultPrevented || event.isComposing || !available.get()) {
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        focusedValue.set(collection.get().next(focusedValue.get()) || "");
        scrollFocusedItemIntoView();
        break;
      case "ArrowUp":
        focusedValue.set(collection.get().prev(focusedValue.get()) || "");
        scrollFocusedItemIntoView();
        break;
      case "Home":
        focusedValue.set(collection.get().first() || "");
        scrollFocusedItemIntoView();
        break;
      case "End":
        focusedValue.set(collection.get().last() || "");
        scrollFocusedItemIntoView();
        break;
      case "Enter":
        {
          const value = focusedValue.get();
          if (value) {
            selectedValue.set(value);
            (_a = listboxItemEmitter.get()) == null ? void 0 : _a();
            (_b = listboxEmitter.get()) == null ? void 0 : _b();
          }
        }
        break;
      default:
        return;
    }
    event.preventDefault();
  };
  useEffect2(element, () => {
    const target = eventTarget.get() || element;
    target.addEventListener("keydown", keydownHandler);
    return () => target.removeEventListener("keydown", keydownHandler);
  });
}
function useMouseMoving(element) {
  const pointerMoving = createSignal(false);
  let lastMoveMoveTime = 0;
  useEventListener2(element, "pointermove", () => {
    lastMoveMoveTime = Date.now();
    pointerMoving.set(true);
  });
  useEventListener2(element, "pointerenter", () => {
    lastMoveMoveTime = Date.now();
    pointerMoving.set(true);
  });
  useEffect2(element, () => {
    if (!pointerMoving.get()) {
      return;
    }
    const id = setInterval(() => {
      if (Date.now() - lastMoveMoveTime > 200) {
        pointerMoving.set(false);
        lastMoveMoveTime = 0;
      }
    }, 50);
    return () => clearInterval(id);
  });
  return pointerMoving;
}

// src/listbox.types.ts
import { defaultItemFilter } from "@aria-ui/collection";
var listboxProps = {
  value: { default: "" },
  selectionMode: { default: "single" },
  autoFocus: { default: false },
  query: { default: "" },
  filter: { default: defaultItemFilter },
  eventTarget: { default: void 0 }
};
var listboxEvents = {
  valueChange: {}
};

// src/elements.ts
var ListboxEmptyElement = class extends defineCustomElement({
  props: listboxEmptyProps,
  events: listboxEmptyEvents,
  setup: useListboxEmpty
}) {
};
var ListboxItemElement = class extends defineCustomElement({
  props: listboxItemProps,
  events: listboxItemEvents,
  setup: useListboxItem
}) {
};
var ListboxElement = class extends defineCustomElement({
  props: listboxProps,
  events: listboxEvents,
  setup: useListbox
}) {
};

export {
  useListboxEmpty,
  listboxEmptyProps,
  listboxEmptyEvents,
  useListboxItem,
  listboxItemProps,
  listboxItemEvents,
  useListbox,
  useCollectionKeydownHandler,
  useMouseMoving,
  listboxProps,
  listboxEvents,
  ListboxEmptyElement,
  ListboxItemElement,
  ListboxElement
};
